import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import EventHero from './components/EventHero';
import EventInfo from './components/EventInfo';
import TicketTiers from './components/TicketTiers';
import BookingPanel from './components/BookingPanel';
import RelatedEvents from './components/RelatedEvents';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { EventService } from '../../services/eventService';

const EventDetails = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  
  // Get event ID from both query params (?id=) and path params (/:eventId)
  const eventIdFromQuery = searchParams?.get('id');
  const eventIdFromPath = params?.eventId;
  const eventId = eventIdFromQuery || eventIdFromPath;
  
  const [selectedTiers, setSelectedTiers] = useState({});
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [error, setError] = useState(null);

  // Helper function to validate UUID format
  const isValidUUID = (uuid) => {
    if (!uuid) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex?.test(uuid);
  };

  // Fetch event data from Supabase
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Validate eventId before making API call
        if (!eventId) {
          setError('Event ID is required');
          setIsLoading(false);
          return;
        }

        if (!isValidUUID(eventId)) {
          setError('Invalid event ID format');
          setIsLoading(false);
          return;
        }

        // Fetch the specific event by ID
        const { data: eventData, error: eventError } = await EventService?.getEventById(eventId);
        
        if (eventError) {
          console.error('Error fetching event:', eventError);
          setError(eventError);
          return;
        }

        if (!eventData) {
          setError('Event not found');
          return;
        }

        // Transform the data to match the expected format for the components
        const transformedEvent = {
          id: eventData?.id,
          title: eventData?.title,
          category: eventData?.category || 'Conference',
          featured: eventData?.isFeatured || false,
          date: eventData?.date ? new Date(eventData?.date)?.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long', 
            day: 'numeric'
          }) : 'TBD',
          time: eventData?.date && eventData?.endDate ? 
            `${new Date(eventData?.date)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - ${new Date(eventData?.endDate)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}` : 
            eventData?.date ? new Date(eventData?.date)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'TBD',
          timezone: 'Local Time',
          duration: eventData?.date && eventData?.endDate ? 
            `${Math.ceil((new Date(eventData?.endDate) - new Date(eventData?.date)) / (1000 * 60 * 60))} hours` : 'TBD',
          ageRequirement: '18+ years', // Default since not in database
          images: eventData?.image ? [eventData?.image] : [
            'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=500&fit=crop'
          ],
          description: eventData?.description || 'No description available.',
          venue: {
            name: eventData?.venue_name || 'Venue TBD',
            address: eventData?.location || '',
            city: eventData?.location?.split(',')?.[eventData?.location?.split(',')?.length - 2]?.trim() || '',
            state: eventData?.location?.split(',')?.[eventData?.location?.split(',')?.length - 1]?.trim() || '',
            coordinates: { lat: 0, lng: 0 }, // Default coordinates
            parking: true
          },
          host: {
            name: eventData?.organizer || 'Event Organizer',
            title: 'Event Host',
            bio: `Experienced event organizer committed to creating memorable experiences.`,
            avatar: eventData?.organizerAvatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
            eventsHosted: 50, // Default value
            rating: 4.8, // Default rating
            followers: 1000 // Default followers
          },
          totalCapacity: eventData?.maxCapacity || 100,
          bookedSeats: eventData?.attendeeCount || 0,
          attendeeCount: eventData?.attendeeCount || 0,
          tags: eventData?.category ? [eventData?.category] : ['Event'],
          tiers: eventData?.tiers?.map(tier => ({
            id: tier?.id,
            name: tier?.name,
            description: tier?.description || `Access to ${tier?.name} tier`,
            price: tier?.price || 0,
            originalPrice: tier?.price ? Math.round(tier?.price * 1.2) : 0, // 20% markup as original
            fees: Math.round((tier?.price || 0) * 0.08), // 8% fees
            capacity: tier?.quantityAvailable || 100,
            sold: tier?.quantitySold || 0,
            status: (tier?.quantityAvailable || 100) > (tier?.quantitySold || 0) ? 'available' : 'sold_out',
            earlyBird: tier?.saleEndDate && new Date(tier?.saleEndDate) > new Date(),
            popular: tier?.type === 'vip' || tier?.name?.toLowerCase()?.includes('vip'),
            benefits: [
              `Access to ${tier?.name}`,
              'Event materials included',
              tier?.type === 'vip' ? 'Premium seating' : 'Standard seating',
              tier?.type === 'vip' ? 'VIP networking access' : 'General networking'
            ]?.filter(Boolean)
          })) || [],
          policies: {
            cancellation: 'Full refund available up to 7 days before the event. 50% refund available up to 3 days before. No refunds within 72 hours of the event.',
            refund: 'Refunds will be processed within 5-7 business days to the original payment method. Processing fees are non-refundable.'
          }
        };

        setCurrentEvent(transformedEvent);

        // Fetch related events (other published events, excluding current one)
        const { data: allEvents } = await EventService?.getAllEvents({ 
          status: 'published'
        });
        
        const filteredRelatedEvents = allEvents?.filter(event => event?.id !== eventId)?.slice(0, 6) || [];
        setRelatedEvents(filteredRelatedEvents);

      } catch (err) {
        console.error('Error loading event data:', err);
        setError(err?.message || 'Failed to load event data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [eventId]);

  const handleTierSelect = (tierId, quantity) => {
    setSelectedTiers(prev => ({
      ...prev,
      [tierId]: quantity
    }));
  };

  const handleDiscountApply = (discount) => {
    setAppliedDiscount(discount);
  };

  const handleBackToDiscovery = () => {
    navigate('/event-discovery');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="animate-pulse">
              <div className="h-96 bg-muted rounded-xl mb-8"></div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-8 bg-muted rounded w-3/4"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-muted rounded"></div>
                    <div className="h-4 bg-muted rounded w-5/6"></div>
                    <div className="h-4 bg-muted rounded w-4/6"></div>
                  </div>
                </div>
                <div className="h-96 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="text-center py-16">
              <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-red-500" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Event Not Found</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button
                variant="default"
                onClick={handleBackToDiscovery}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Events
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main content - only render if currentEvent exists
  if (!currentEvent) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="text-center py-16">
              <Icon name="Calendar" size={64} className="mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold text-foreground mb-2">No Event Data</h2>
              <p className="text-muted-foreground mb-6">Unable to load event information.</p>
              <Button
                variant="default"
                onClick={handleBackToDiscovery}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Events
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
            <button
              onClick={handleBackToDiscovery}
              className="hover:text-primary transition-colors"
            >
              Events
            </button>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground">{currentEvent?.title}</span>
          </div>

          {/* Event Hero */}
          <EventHero event={currentEvent} />

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            {/* Left Column - Event Details */}
            <div className="lg:col-span-2">
              <EventInfo event={currentEvent} />
              
              {/* Ticket Selection */}
              <div className="mt-12" data-ticket-tiers>
                <TicketTiers
                  tiers={currentEvent?.tiers}
                  onTierSelect={handleTierSelect}
                  selectedTiers={selectedTiers}
                />
              </div>
            </div>

            {/* Right Column - Booking Panel */}
            <div className="lg:col-span-1">
              <BookingPanel
                event={currentEvent}
                selectedTiers={selectedTiers}
                onDiscountApply={handleDiscountApply}
              />
            </div>
          </div>

          {/* Related Events */}
          <RelatedEvents
            events={relatedEvents}
            currentEventId={currentEvent?.id}
          />
        </div>
      </div>
      
      {/* Mobile Sticky Booking Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 z-40">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground">Starting from</div>
            <div className="font-semibold text-foreground">
              ${currentEvent?.tiers?.length > 0 ? 
                Math.min(...currentEvent?.tiers?.map(tier => tier?.price || 0))?.toFixed(2) : 
                '0.00'}
            </div>
          </div>
          <Button
            variant="default"
            size="lg"
            onClick={() => {
              const element = document.querySelector('[data-ticket-tiers]');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            iconName="Ticket"
            iconPosition="left"
          >
            Select Tickets
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;